import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Wallet,
  Zap,
  Bus,
  Rocket,
  Star,
  Crown,
  Loader2,
  X,
  CheckCircle2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useI18n } from "@/lib/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useOverlayLock } from "@/contexts/UiOverlayContext";
import {
  usePaymentsMode,
  usePaymentsConfig,
  useCreateCheckoutSession,
  useMockCheckout,
  usePurchaseBooster,
  TOKEN_BUNDLES,
  BOOSTERS,
  SUBSCRIPTIONS,
} from "@/lib/wallet";

// === LOCAL SANDBOX — remove before production deploy ===
// Maps the shipped Phase 19b internal booster keys → the sandbox SKUs
// accepted by `POST /api/payments/mock-checkout`. Any drift in either
// key breaks the sandbox booster acquire flow, so this map is the
// single source of truth on the client side.
const SANDBOX_BOOSTER_SKU = {
  proximity_lightning: "booster_proximity_lightning",
  streak_minibus: "booster_racha_mini_bus",
  quick_boost_3d: "booster_quick_boost",
};

/**
 * Phase 19b — Premium Shop modal.
 *
 * Reachable only via the wallet button in AppShell. Uses the payments
 * `mode` probe to decide between real Stripe checkout, "Coming soon"
 * gated state (beta not yet), or full simulation.
 *
 * Boosters remain fully functional in every mode — they're the in-app
 * currency sink (spending tokens the user already earned).
 *
 * === LOCAL SANDBOX — remove before production deploy ===
 * When `/api/payments/config` reports `mock_checkout_enabled: true`, the
 * "Buy" / "Subscribe" buttons hit `/api/payments/mock-checkout` instead
 * of Stripe. A prominent SANDBOX badge sits next to the shop title so
 * the operator never confuses this with a live payment.
 */
export default function PremiumShopModal({ open, onOpenChange }) {
  useOverlayLock(open);
  const { t } = useI18n();
  const { user, refetch } = useAuth();
  const modeQuery = usePaymentsMode();
  const configQuery = usePaymentsConfig();
  const checkoutMutation = useCreateCheckoutSession();
  const mockCheckoutMutation = useMockCheckout();
  const boosterMutation = usePurchaseBooster();

  const [purchasing, setPurchasing] = useState(null); // sku or booster key currently pending

  // Reset local state on close.
  useEffect(() => {
    if (!open) setPurchasing(null);
  }, [open]);

  const balance = user?.figutokens_balance ?? 0;
  const activeBooster = user?.active_booster ?? "none";
  const boosterExpires = user?.booster_expires_at ?? null;
  const boosterActive = useMemo(() => {
    if (!activeBooster || activeBooster === "none") return false;
    if (!boosterExpires) return false;
    return new Date(boosterExpires).getTime() > Date.now();
  }, [activeBooster, boosterExpires]);

  const sandboxMode = !!configQuery.data?.mock_checkout_enabled;
  const isSim = modeQuery.data?.mode === "simulation";
  const isBetaGated =
    modeQuery.data?.mode === "stripe_beta" &&
    !modeQuery.data?.user_is_beta_tester;
  // In sandbox mode the buy buttons are ALWAYS unlocked (that's the
  // whole point of this Premium Shop evaluation pass).
  const stripeDisabled = sandboxMode ? false : isSim || isBetaGated;

  const buyOrGate = async (sku) => {
    setPurchasing(sku);
    try {
      // === LOCAL SANDBOX branch ===
      if (sandboxMode) {
        const result = await mockCheckoutMutation.mutateAsync({ sku });
        await refetch();
        if (result?.status === "credited") {
          const credit = result.credit ?? 0;
          toast.success(`Credited ${credit} FiguTokens (SANDBOX)`);
        } else if (result?.status === "subscription_activated") {
          const tier = result.tier || "pro";
          toast.success(`Subscription set to ${tier} (SANDBOX)`);
        } else {
          toast.info("Mock checkout completed (SANDBOX)");
        }
        return;
      }
      // Real Stripe branch.
      const result = await checkoutMutation.mutateAsync({ sku });
      if (result.status === "checkout" && result.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        toast.info(t("shop.coming_soon"));
      }
    } catch (e) {
      const code =
        e?.response?.data?.detail?.error_code ||
        e?.response?.data?.detail ||
        "";
      if (code === "cooldown") {
        toast.error("Too fast — try again in a moment.");
      } else {
        toast.error(t("shop.checkout_error", "Checkout failed. Try again."));
      }
    } finally {
      setPurchasing(null);
    }
  };

  const activateBooster = async (boosterKey) => {
    setPurchasing(boosterKey);
    try {
      // === LOCAL SANDBOX branch — route through /payments/mock-checkout
      // so all sandbox activity is centralized in stripe_events_processed.
      if (sandboxMode) {
        const sandboxSku = SANDBOX_BOOSTER_SKU[boosterKey];
        const result = await mockCheckoutMutation.mutateAsync({ sku: sandboxSku });
        toast.success(
          `Booster acquired: ${result?.friendly_name || boosterKey} (SANDBOX)`
        );
        await refetch();
        return;
      }
      // Real path — Phase 19b /boosters/purchase.
      await boosterMutation.mutateAsync({ booster: boosterKey });
      toast.success(t("shop.booster_activated"));
      await refetch();
    } catch (err) {
      const detail = err?.response?.data?.detail || {};
      const code = detail.code || detail.error_code || detail;
      if (code === "insufficient_balance") {
        // Prefer the server's helpful "Need X more FiguTokens." string.
        const msg = detail.message || t("shop.insufficient_balance");
        toast.error(msg);
      } else if (code === "booster_already_active") {
        toast.error(t("shop.booster_already_active"));
        await refetch();
      } else if (code === "cooldown") {
        toast.error("Too fast — try again in a moment.");
      } else {
        toast.error(t("shop.checkout_error", "Something went wrong."));
      }
    } finally {
      setPurchasing(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onOpenChange(false);
      }}
    >
      <DialogContent
        data-testid="premium-shop-modal"
        className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl p-0 overflow-hidden [&>button:last-child]:hidden max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/5 sticky top-0 bg-[#0A0A0A] z-10">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-[#E5B83A]/15 border border-[#E5B83A]/30 flex items-center justify-center">
              <Wallet className="h-5 w-5 text-[#E5B83A]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <DialogTitle
                  data-testid="shop-title"
                  className="font-display text-lg font-bold text-white"
                >
                  {t("shop.title")}
                </DialogTitle>
                {sandboxMode ? (
                  <span
                    data-testid="shop-sandbox-badge"
                    className="inline-flex items-center gap-1 rounded-full border border-amber-400/60 bg-amber-400/10 px-2 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-widest text-amber-300"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                    SANDBOX
                  </span>
                ) : null}
              </div>
              <DialogDescription className="text-[#A1A1AA] font-body text-xs">
                {sandboxMode
                  ? "Mock payments — balance credits and tier upgrades run locally. No card is charged."
                  : t("shop.subtitle", "Fuel your trades.")}
              </DialogDescription>
            </div>
            <button
              type="button"
              data-testid="shop-close"
              onClick={() => onOpenChange(false)}
              className="rounded-md border border-white/10 text-white/70 hover:bg-white/5 h-9 w-9 inline-flex items-center justify-center"
              aria-label={t("buttons.close")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </DialogHeader>

        <div className="px-6 py-5 space-y-6">
          {/* Balance strip */}
          <div
            data-testid="shop-balance-strip"
            className="rounded-lg border border-[#E5B83A]/40 bg-[#E5B83A]/5 px-4 py-3 flex items-center justify-between"
          >
            <p className="text-xs uppercase tracking-widest text-[#E5B83A]">
              {t("shop.balance_label")}
            </p>
            <p
              data-testid="shop-balance-value"
              className="font-mono text-2xl font-bold text-[#E5B83A]"
            >
              {balance} {t("wallet.figutokens_label")}
            </p>
          </div>

          {/* ── Token bundles ─── */}
          <section>
            <h3
              data-testid="shop-section-tokens"
              className="font-display text-sm font-bold uppercase tracking-wider text-white/80 mb-3"
            >
              {t("shop.token_bundles_title")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {TOKEN_BUNDLES.map((b) => (
                <ShopCard
                  key={b.sku}
                  testId={`shop-bundle-${b.sku}`}
                  icon={
                    b.sku === "tokens_1500" ? (
                      <Crown className="h-5 w-5 text-[#E5B83A]" />
                    ) : (
                      <Star className="h-5 w-5 text-[#E5B83A]" />
                    )
                  }
                  title={t(`shop.${b.sku}_name`)}
                  subtitle={`+${b.tokens} ${t("wallet.figutokens_label")}`}
                  price={b.price_display}
                  ctaLabel={
                    stripeDisabled ? t("shop.coming_soon") : t("shop.buy")
                  }
                  ctaTestId={`shop-buy-${b.sku}`}
                  disabled={stripeDisabled || purchasing === b.sku}
                  loading={purchasing === b.sku}
                  onClick={() => buyOrGate(b.sku)}
                />
              ))}
            </div>
          </section>

          {/* ── Boosters ─── */}
          <section>
            <h3
              data-testid="shop-section-boosters"
              className="font-display text-sm font-bold uppercase tracking-wider text-white/80 mb-3"
            >
              {t("shop.boosters_title")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {BOOSTERS.map((b) => {
                const isThisActive =
                  boosterActive && activeBooster === b.key;
                const otherActive = boosterActive && !isThisActive;
                const affordable = balance >= b.cost;
                const iconMap = {
                  proximity_lightning: (
                    <Zap className="h-5 w-5 text-[#E5B83A]" />
                  ),
                  streak_minibus: (
                    <Bus className="h-5 w-5 text-[#E5B83A]" />
                  ),
                  quick_boost_3d: (
                    <Rocket className="h-5 w-5 text-[#E5B83A]" />
                  ),
                };
                let cta = t("shop.activate");
                if (isThisActive) {
                  const dt = new Date(boosterExpires).toLocaleString();
                  cta = t("shop.booster_active_until").replace("{date}", dt);
                } else if (otherActive) {
                  cta = t("shop.booster_already_active");
                } else if (!affordable) {
                  cta = t("shop.insufficient_balance_short", "Need more");
                }
                return (
                  <ShopCard
                    key={b.key}
                    testId={`shop-booster-${b.key}`}
                    icon={iconMap[b.key]}
                    title={t(`shop.${b.key}_name`)}
                    subtitle={t(`shop.${b.key}_desc`)}
                    price={`${b.cost} ${t("wallet.figutokens_label")}`}
                    ctaLabel={cta}
                    ctaTestId={`shop-activate-${b.key}`}
                    ctaVariant={isThisActive ? "active" : "primary"}
                    disabled={
                      isThisActive ||
                      otherActive ||
                      !affordable ||
                      purchasing === b.key
                    }
                    loading={purchasing === b.key}
                    onClick={() => activateBooster(b.key)}
                  />
                );
              })}
            </div>
          </section>

          {/* ── Subscriptions ─── */}
          <section>
            <h3
              data-testid="shop-section-subs"
              className="font-display text-sm font-bold uppercase tracking-wider text-white/80 mb-3"
            >
              {t("shop.subscriptions_title")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SUBSCRIPTIONS.map((s) => (
                <ShopCard
                  key={s.sku}
                  testId={`shop-sub-${s.sku}`}
                  icon={<Crown className="h-5 w-5 text-[#E5B83A]" />}
                  title={t(`shop.${s.sku}_name`)}
                  subtitle={t(`shop.${s.sku}_features`)}
                  price={s.price_display}
                  ctaLabel={
                    stripeDisabled ? t("shop.coming_soon") : t("shop.subscribe")
                  }
                  ctaTestId={`shop-subscribe-${s.sku}`}
                  disabled={stripeDisabled || purchasing === s.sku}
                  loading={purchasing === s.sku}
                  onClick={() => buyOrGate(s.sku)}
                />
              ))}
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShopCard({
  testId,
  icon,
  title,
  subtitle,
  price,
  ctaLabel,
  ctaTestId,
  ctaVariant = "primary",
  disabled,
  loading,
  onClick,
}) {
  return (
    <div
      data-testid={testId}
      className="rounded-lg border border-white/10 bg-white/[0.02] p-4 flex flex-col justify-between gap-3"
    >
      <div>
        <div className="flex items-center gap-2 mb-2">
          {icon}
          <p className="font-body text-sm font-semibold text-white">{title}</p>
        </div>
        <p className="text-xs text-[#A1A1AA] leading-snug min-h-[32px]">
          {subtitle}
        </p>
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-white/5">
        <p className="font-mono text-sm text-[#E5B83A]">{price}</p>
        <button
          type="button"
          data-testid={ctaTestId}
          onClick={onClick}
          disabled={disabled}
          style={{ minHeight: "44px", minWidth: "44px" }}
          className={
            "inline-flex items-center gap-1.5 px-3 rounded-md font-body font-semibold text-xs transition-colors " +
            (ctaVariant === "active"
              ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 cursor-default"
              : disabled
              ? "bg-white/5 border border-white/10 text-white/40 cursor-not-allowed"
              : "bg-[#E5B83A] text-black hover:bg-[#D4AF37]")
          }
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : ctaVariant === "active" ? (
            <CheckCircle2 className="h-3.5 w-3.5" />
          ) : null}
          <span className="truncate max-w-[160px]">{ctaLabel}</span>
        </button>
      </div>
    </div>
  );
}
